import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getCurrentUser } from '../services/authService';
import { useWindowSize, BP } from '../hooks/useWindowSize';

function I9SchedulePage() {
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const isMobile = width < BP.mobile;
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null); // i9RequestId currently being updated

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(
          // Prompt #451: was appointment_status-only — returned every
          // employer's scheduled I-9s (a cross-tenant leak). Now scoped to
          // the signed-in employer, which the tightened rule also requires.
          query(
            collection(db, 'i9_requests'),
            where('appointment_status', '==', 'scheduled'),
            where('employerId', '==', getCurrentUser()?.uid)
          )
        );

        const rows = await Promise.all(
          snap.docs.map(async (d) => {
            const data = d.data();
            // HC-INFRA-008C, Prompt #422 — reads the public projection
            // (publicWorkerProfiles/{uid}, Prompt #419), not the raw
            // users/{uid} doc. displayName is the only name field this
            // projection ever carries (no firstName/lastName, no email) —
            // the projection doesn't exist at all for a worker who hasn't
            // completed onboarding, handled the same as any other
            // not-found case here: falls back to 'Unknown', not a crash.
            let workerName = 'Unknown';
            try {
              const workerDoc = await getDoc(doc(db, 'publicWorkerProfiles', data.workerId));
              if (workerDoc.exists()) {
                workerName = workerDoc.data().displayName || 'Unknown';
              }
            } catch (_) {}
            return {
              id: d.id,
              workerName,
              jobId: data.jobId,
              appointmentTime: data.appointment_time,
            };
          })
        );

        // Sort ascending by appointment time (client-side — avoids composite index)
        rows.sort((a, b) => {
          const aMs = a.appointmentTime?.toMillis?.() ?? 0;
          const bMs = b.appointmentTime?.toMillis?.() ?? 0;
          return aMs - bMs;
        });

        setAppointments(rows);
      } catch (err) {
        console.error('Error loading I-9 schedule:', err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const markCompleted = async (i9RequestId) => {
    setUpdating(i9RequestId);
    try {
      await updateDoc(doc(db, 'i9_requests', i9RequestId), {
        appointment_status: 'completed',
        workbrightStatus: 'complete', // matches existing codebase enum value
        last_updated: serverTimestamp(),
      });
      setAppointments((prev) => prev.filter((a) => a.id !== i9RequestId));
    } catch (err) {
      console.error('Error marking I-9 completed:', err);
      alert('Failed to update status. Please try again.');
    } finally {
      setUpdating(null);
    }
  };

  const markNoShow = async (i9RequestId) => {
    setUpdating(i9RequestId);
    try {
      await updateDoc(doc(db, 'i9_requests', i9RequestId), {
        appointment_status: 'no_show',
        last_updated: serverTimestamp(),
      });
      setAppointments((prev) => prev.filter((a) => a.id !== i9RequestId));
    } catch (err) {
      console.error('Error marking I-9 no-show:', err);
      alert('Failed to update status. Please try again.');
    } finally {
      setUpdating(null);
    }
  };

  const formatAppointmentTime = (ts) => {
    if (!ts) return '—';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div style={{ overflowX: 'hidden', maxWidth: '100vw', boxSizing: 'border-box' }}>
      {loading ? (
        <div style={{ textAlign: 'center', color: '#6b7280', padding: '3rem' }}>
          Loading appointments…
        </div>
      ) : appointments.length === 0 ? (
        <div style={{
          backgroundColor: '#F8FAFC',
          borderRadius: '8px',
          padding: '3rem',
          textAlign: 'center',
          color: '#6b7280',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <p style={{ fontSize: '18px', marginBottom: '0.5rem', fontWeight: '500' }}>
            No appointments scheduled
          </p>
          <p style={{ fontSize: '14px', margin: 0 }}>
            Scheduled I-9 appointments will appear here once employers book them.
          </p>
        </div>
      ) : isMobile ? (
        /* Mobile: card layout */
        <div>
          {appointments.map((appt) => (
            <div key={appt.id} style={{ backgroundColor: '#F8FAFC', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', marginBottom: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#0369a1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '600', fontSize: '16px', flexShrink: 0 }}>
                  {appt.workerName.charAt(0).toUpperCase()}
                </div>
                <div style={{ fontWeight: '500', fontSize: '14px', color: '#0F1E2E' }}>{appt.workerName}</div>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <span style={{ display: 'inline-block', padding: '0.25rem 0.625rem', backgroundColor: '#eff6ff', color: '#1d4ed8', borderRadius: '9999px', fontSize: '13px', fontWeight: '500', border: '1px solid #bfdbfe' }}>
                  {formatAppointmentTime(appt.appointmentTime)}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button onClick={() => markCompleted(appt.id)} disabled={updating === appt.id} style={{ backgroundColor: updating === appt.id ? '#a0aec0' : '#10b981', color: 'white', padding: '0.375rem 0.75rem', borderRadius: '4px', border: 'none', cursor: updating === appt.id ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '500' }}>
                  {updating === appt.id ? 'Saving…' : 'Mark Completed'}
                </button>
                <button onClick={() => markNoShow(appt.id)} disabled={updating === appt.id} style={{ backgroundColor: updating === appt.id ? '#a0aec0' : '#ef4444', color: 'white', padding: '0.375rem 0.75rem', borderRadius: '4px', border: 'none', cursor: updating === appt.id ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '500' }}>
                  No Show
                </button>
                <button onClick={() => navigate(`/jobs/${appt.jobId}/applicants`)} disabled={updating === appt.id} style={{ backgroundColor: '#0369a1', color: 'white', padding: '0.375rem 0.75rem', borderRadius: '4px', border: 'none', cursor: updating === appt.id ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '500' }}>
                  View Application
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Desktop: table */
        <div style={{
          backgroundColor: '#F8FAFC',
          borderRadius: '8px',
          overflowX: 'auto',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
            <thead style={{ backgroundColor: '#f9fafb' }}>
              <tr>
                <th style={thStyle}>Candidate Name</th>
                <th style={thStyle}>Appointment Time</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {appointments.map((appt, index) => (
                <tr key={appt.id} style={{ borderTop: index > 0 ? '1px solid #e5e7eb' : 'none' }}>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        backgroundColor: '#0369a1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '16px',
                        flexShrink: 0,
                      }}>
                        {appt.workerName.charAt(0).toUpperCase()}
                      </div>
                      <span style={{ fontWeight: '500' }}>{appt.workerName}</span>
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.625rem',
                      backgroundColor: '#eff6ff',
                      color: '#1d4ed8',
                      borderRadius: '9999px',
                      fontSize: '13px',
                      fontWeight: '500',
                      border: '1px solid #bfdbfe',
                    }}>
                      {formatAppointmentTime(appt.appointmentTime)}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => markCompleted(appt.id)}
                        disabled={updating === appt.id}
                        style={{
                          backgroundColor: updating === appt.id ? '#a0aec0' : '#10b981',
                          color: 'white',
                          padding: '0.375rem 0.75rem',
                          borderRadius: '4px',
                          border: 'none',
                          cursor: updating === appt.id ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                        }}
                      >
                        {updating === appt.id ? 'Saving…' : 'Mark Completed'}
                      </button>
                      <button
                        onClick={() => markNoShow(appt.id)}
                        disabled={updating === appt.id}
                        style={{
                          backgroundColor: updating === appt.id ? '#a0aec0' : '#ef4444',
                          color: 'white',
                          padding: '0.375rem 0.75rem',
                          borderRadius: '4px',
                          border: 'none',
                          cursor: updating === appt.id ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                        }}
                      >
                        No Show
                      </button>
                      <button
                        onClick={() => navigate(`/jobs/${appt.jobId}/applicants`)}
                        disabled={updating === appt.id}
                        style={{
                          backgroundColor: '#0369a1',
                          color: 'white',
                          padding: '0.375rem 0.75rem',
                          borderRadius: '4px',
                          border: 'none',
                          cursor: updating === appt.id ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                        }}
                      >
                        View Application
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  padding: '0.75rem 1rem',
  textAlign: 'left',
  fontWeight: '600',
  color: '#374151',
  fontSize: '14px',
};

const tdStyle = {
  padding: '1rem',
  verticalAlign: 'middle',
};

export default I9SchedulePage;
